import { normalizeNewsletterTextAlignment, type NewsletterTextAlignment, renderNewsletterText, normalizeNewsletterImageHeight } from "../../../packages/core/src/newsletterText";
import {
  resolveAssistantBulletinPalette,
  type AssistantThemeSettings,
  resolveAssistantSectionMediaRatio,
  resolveAssistantThemeTokens,
} from "./newsletterUtils";

type AssistantSectionLayout = "left" | "right" | "stacked";

type AssistantSectionDraft = {
  text_align?: NewsletterTextAlignment;
  title: string;
  body: string;
  body_html?: string;
  image_url: string;
  image_alt: string;
  button_label: string;
  button_url: string;
  media_layout: AssistantSectionLayout;
  media_width_ratio: number | null;
  media_height?: number | null;
};

type AssistantDraftPayload = {
  text_align?: NewsletterTextAlignment;
  theme: AssistantThemeSettings;
  header_logo_url: string;
  header_logo_alt: string;
  intro_kicker: string;
  intro_title: string;
  intro_body: string;
  intro_body_html?: string;
  intro_image_url: string;
  intro_image_alt: string;
  intro_image_layout: AssistantSectionLayout;
  intro_image_width_ratio: number | null;
  intro_image_height?: number | null;
  sections: AssistantSectionDraft[];
  closing_note: string;
  closing_note_html?: string;
};

function renderAlignedText(text: string, html: string | undefined, alignment: unknown) {
  return `<div style="text-align:${normalizeNewsletterTextAlignment(alignment) || "left"};">${renderNewsletterText(text, html)}</div>`;
}

function escapeHtml(value: string) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeHtmlWithBreaks(value: string) {
  return escapeHtml(value).replace(/\n/g, "<br />");
}

function sectionHasContent(section: AssistantSectionDraft) {
  return Boolean(
    section.title.trim() ||
      section.body.trim() ||
      section.image_url.trim() ||
      (section.button_label.trim() && section.button_url.trim())
  );
}

function buildPreviewText(draft: AssistantDraftPayload, sections: AssistantSectionDraft[]) {
  return (
    draft.intro_title.trim() ||
    draft.intro_body.trim() ||
    sections[0]?.title?.trim() ||
    "Newsletter"
  );
}

function buildHeaderLogoHtml(draft: AssistantDraftPayload) {
  if (!draft.header_logo_url.trim()) {
    return "";
  }

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:18px;border-collapse:collapse;">
      <tr>
        <td align="center" style="padding:0;">
          <img
            src="${escapeHtml(draft.header_logo_url.trim())}"
            alt="${escapeHtml(draft.header_logo_alt.trim() || "Logo newsletter")}"
            width="220"
            style="display:block;margin:0 auto;width:220px;max-width:100%;height:auto;border:0;"
          />
        </td>
      </tr>
    </table>
  `.trim();
}

function buildImageHtml(
  section: AssistantSectionDraft,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>,
  maxWidth: string,
  borderRadius = "18px"
) {
  if (!section.image_url.trim()) {
    return "";
  }

  return `
    <img
      src="${escapeHtml(section.image_url.trim())}"
      alt="${escapeHtml(section.image_alt.trim() || section.title.trim() || "Illustration")}"
      style="${section.media_layout === "stacked" ? `display:block;width:auto;max-width:100%;height:auto;max-height:${normalizeNewsletterImageHeight(section.media_height)}px;margin:0 auto;` : `display:block;width:100%;max-width:${maxWidth};height:auto;`}border:0;border-radius:${borderRadius};"
    />
  `.trim();
}

function buildIntroImageHtml(
  draft: AssistantDraftPayload,
  maxWidth = "100%",
  borderRadius = "18px",
  extraStyle = ""
) {
  if (!draft.intro_image_url.trim()) {
    return "";
  }

  return `
    <img
      src="${escapeHtml(draft.intro_image_url.trim())}"
      alt="${escapeHtml(draft.intro_image_alt.trim() || draft.intro_title.trim() || "Illustration d'introduction")}"
      style="${draft.intro_image_layout === "stacked" ? `display:block;width:auto;max-width:100%;height:auto;max-height:${normalizeNewsletterImageHeight(draft.intro_image_height)}px;margin:0 auto;` : `display:block;width:100%;max-width:${maxWidth};height:auto;`}border:0;border-radius:${borderRadius};${extraStyle}"
    />
  `.trim();
}

function buildButtonHtml(
  section: AssistantSectionDraft,
  theme: AssistantThemeSettings,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>
) {
  if (!section.button_label.trim() || !section.button_url.trim()) {
    return "";
  }

  const isFestive = theme.style === "festive";
  const buttonStyleByMode =
    isFestive
      ? `display:inline-block;min-width:176px;padding:12px 20px;border-radius:${themeTokens.buttonRadius};background:${themeTokens.buttonBackground};color:${themeTokens.buttonText};font-weight:800;text-decoration:${themeTokens.buttonTextDecoration};font-family:${themeTokens.bodyFont};border:1px solid ${themeTokens.buttonBorder};text-align:center;`
      : themeTokens.buttonMode === "outline"
      ? `display:inline-block;padding:12px 18px;border-radius:${themeTokens.buttonRadius};background:transparent;color:${themeTokens.buttonText};font-weight:700;text-decoration:${themeTokens.buttonTextDecoration};font-family:${themeTokens.bodyFont};border:1px solid ${themeTokens.buttonBorder};`
      : themeTokens.buttonMode === "ghost"
        ? `display:inline-block;padding:0;border-radius:${themeTokens.buttonRadius};background:transparent;color:${themeTokens.buttonText};font-weight:700;text-decoration:${themeTokens.buttonTextDecoration};font-family:${themeTokens.bodyFont};border:0;`
        : `display:inline-block;padding:12px 18px;border-radius:${themeTokens.buttonRadius};background:${themeTokens.buttonBackground};color:${themeTokens.buttonText};font-weight:700;text-decoration:${themeTokens.buttonTextDecoration};font-family:${themeTokens.bodyFont};border:1px solid ${themeTokens.buttonBorder};`;

  return `
    <div style="margin-top:${isFestive ? "18px" : "16px"};${isFestive ? "text-align:center;" : ""}">
      <a
        href="${escapeHtml(section.button_url.trim())}"
        style="${buttonStyleByMode}"
      >
        ${escapeHtml(section.button_label.trim())}
      </a>
    </div>
  `.trim();
}

function buildBodyHtml(
  section: AssistantSectionDraft,
  theme: AssistantThemeSettings,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>,
  options: {
    textColor?: string;
    textAlign?: NewsletterTextAlignment;
    fontWeight?: string;
  } = {}
) {
  const textAlign = normalizeNewsletterTextAlignment(section.text_align) || options.textAlign || "left";
  const fontWeight = options.fontWeight || "400";

  return `
    ${section.body.trim()
      ? `<div style="color:${options.textColor || themeTokens.muted};font-size:${themeTokens.bodySize};line-height:${themeTokens.bodyLineHeight};font-family:${themeTokens.bodyFont};text-align:${textAlign};font-weight:${fontWeight};">${renderNewsletterText(section.body, section.body_html)}</div>`
      : ""}
    ${buildButtonHtml(section, theme, themeTokens)}
  `.trim();
}

function buildCopyHtml(
  section: AssistantSectionDraft,
  theme: AssistantThemeSettings,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>
) {
  const isFestive = theme.style === "festive";

  return `
    ${section.title.trim()
      ? `<h2 style="margin:0 0 12px;color:${themeTokens.text};font-size:${themeTokens.sectionTitleSize};line-height:${isFestive ? "1.1" : "1.2"};font-weight:${isFestive ? "900" : "700"};font-family:${themeTokens.headingFont};${isFestive ? "text-transform:uppercase;letter-spacing:0.03em;text-align:center;" : ""}">${escapeHtml(section.title.trim())}</h2>`
      : ""}
    ${buildBodyHtml(section, theme, themeTokens, {
      textColor: themeTokens.muted,
      textAlign: isFestive ? "center" : "left",
      fontWeight: isFestive ? "700" : "400",
    })}
  `.trim();
}

function buildBulletinIntroHtml(
  draft: AssistantDraftPayload,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>
) {
  const palette = resolveAssistantBulletinPalette(draft.theme);
  const introImageHtml = buildIntroImageHtml(draft, "100%", "0");
  const introCopyHtml = `
    ${draft.intro_kicker.trim()
      ? `<div style="margin:0 0 12px;color:${palette.muted};font-size:13px;letter-spacing:${themeTokens.kickerSpacing};text-transform:uppercase;font-family:${themeTokens.bodyFont};font-weight:700;">${escapeHtml(draft.intro_kicker.trim())}</div>`
      : ""}
    ${draft.intro_title.trim()
      ? `<div style="margin:0 0 12px;padding:12px 16px;background:${palette.heroBanner};color:${palette.heroBannerText};font-size:${themeTokens.heroTitleSize};line-height:1.1;font-weight:700;font-family:${themeTokens.headingFont};">${escapeHtml(draft.intro_title.trim())}</div>`
      : ""}
    <div style="height:8px;background:${palette.heroAccent};margin:0 0 12px;"></div>
    ${draft.intro_body.trim()
      ? `<div style="margin:0;color:${themeTokens.heroBodyText};font-size:${themeTokens.bodySize};line-height:${themeTokens.bodyLineHeight};font-family:${themeTokens.bodyFont};">${renderAlignedText(draft.intro_body, draft.intro_body_html, draft.text_align)}</div>`
      : ""}
  `.trim();

  if (introImageHtml && draft.intro_image_layout !== "stacked") {
    const imageWidthPercent = Math.round(resolveAssistantSectionMediaRatio({
      media_width_ratio: draft.intro_image_width_ratio,
    } as AssistantSectionDraft, draft.theme) * 100);
    const imageCell = `<td style="padding:0;vertical-align:top;width:${imageWidthPercent}%;line-height:0;font-size:0;background:#ffffff;">${introImageHtml}</td>`;
    const copyCell = `<td style="padding:16px;vertical-align:top;background:#ffffff;">${introCopyHtml}</td>`;

    return `
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border:1px solid ${palette.border};background:#ffffff;">
        <tr>
          ${draft.intro_image_layout === "right" ? copyCell : imageCell}
          <td style="width:20px;padding:0;background:#ffffff;"></td>
          ${draft.intro_image_layout === "right" ? imageCell : copyCell}
        </tr>
      </table>
    `.trim();
  }

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border:1px solid ${palette.border};background:#ffffff;">
      ${introImageHtml
        ? `<tr>
            <td style="padding:0;line-height:0;font-size:0;">
              ${introImageHtml}
            </td>
          </tr>`
        : ""}
      ${draft.intro_kicker.trim()
        ? `<tr>
            <td style="padding:14px 16px 0;color:${palette.muted};font-size:13px;letter-spacing:${themeTokens.kickerSpacing};text-transform:uppercase;font-family:${themeTokens.bodyFont};font-weight:700;">
              ${escapeHtml(draft.intro_kicker.trim())}
            </td>
          </tr>`
        : ""}
      ${draft.intro_title.trim()
        ? `<tr>
            <td style="padding:12px 16px;background:${palette.heroBanner};color:${palette.heroBannerText};font-size:${themeTokens.heroTitleSize};line-height:1.1;font-weight:700;font-family:${themeTokens.headingFont};">
              ${escapeHtml(draft.intro_title.trim())}
            </td>
          </tr>
          <tr>
            <td style="height:8px;padding:0;background:${palette.heroAccent};line-height:0;font-size:0;">&nbsp;</td>
          </tr>`
        : ""}
      ${draft.intro_body.trim()
        ? `<tr>
            <td style="padding:16px;color:${palette.muted};font-size:${themeTokens.bodySize};line-height:${themeTokens.bodyLineHeight};font-family:${themeTokens.bodyFont};">
              ${renderAlignedText(draft.intro_body, draft.intro_body_html, draft.text_align)}
            </td>
          </tr>`
        : ""}
    </table>
  `.trim();
}

function buildBulletinSectionHtml(
  section: AssistantSectionDraft,
  theme: AssistantThemeSettings,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>,
  sectionIndex: number
) {
  const palette = resolveAssistantBulletinPalette(theme);
  const tone = palette.sectionTones[sectionIndex % palette.sectionTones.length];
  const imageBorderRadius = "6px";
  const imageHtml = buildImageHtml(section, themeTokens, "100%", imageBorderRadius);
  const bodyHtml = buildBodyHtml(section, theme, themeTokens, {
    textColor: tone.surfaceText,
  });
  const titleBandHtml = section.title.trim()
    ? `
      <tr>
        <td style="padding:12px 16px;background:${tone.background};color:${tone.text};font-size:${themeTokens.sectionTitleSize};line-height:1.15;font-weight:700;font-family:${themeTokens.headingFont};">
          ${escapeHtml(section.title.trim())}
        </td>
      </tr>
    `.trim()
    : "";

  if (section.media_layout === "stacked" || !imageHtml) {
    return `
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border:1px solid ${palette.border};background:#ffffff;">
        ${titleBandHtml}
        <tr>
          <td style="padding:16px;background:#ffffff;">
            ${imageHtml ? `<div style="margin-bottom:14px;">${imageHtml}</div>` : ""}
            ${bodyHtml}
          </td>
        </tr>
      </table>
    `.trim();
  }

  const imageWidthPercent = Math.round(resolveAssistantSectionMediaRatio(section, theme) * 100);
  const imageCell = `
    <td style="width:${imageWidthPercent}%;vertical-align:top;padding:0;">
      ${imageHtml}
    </td>
  `.trim();
  const copyCell = `
    <td style="vertical-align:top;padding:0;color:${tone.surfaceText};">
      ${bodyHtml}
    </td>
  `.trim();

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border:1px solid ${palette.border};background:#ffffff;">
      ${titleBandHtml}
      <tr>
        <td style="padding:16px;background:#ffffff;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">
            <tr>
              ${section.media_layout === "right" ? copyCell : imageCell}
              <td style="width:20px;padding:0;background:#ffffff;"></td>
              ${section.media_layout === "right" ? imageCell : copyCell}
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();
}

function buildSectionHtmlWithTheme(
  section: AssistantSectionDraft,
  theme: AssistantThemeSettings,
  themeTokens: ReturnType<typeof resolveAssistantThemeTokens>,
  sectionIndex: number
) {
  const isFestive = theme.style === "festive";
  const isBulletin = theme.style === "bulletin";

  if (isBulletin) {
    return buildBulletinSectionHtml(section, theme, themeTokens, sectionIndex);
  }

  const imageHtml = buildImageHtml(section, themeTokens, isFestive ? "100%" : themeTokens.mediaWidth, isFestive ? "0" : "18px");
  const copyHtml = buildCopyHtml(section, theme, themeTokens);
  const cardStyle = `margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border:1px solid ${themeTokens.border};border-radius:${themeTokens.radius};background:${themeTokens.card};${themeTokens.cardShadow !== "transparent" ? `box-shadow:${themeTokens.cardShadow};` : ""}`;

  if (isFestive) {
    const toneBackground = sectionIndex % 2 === 0 ? themeTokens.primary : themeTokens.secondary;
    const toneText = sectionIndex % 2 === 0 ? themeTokens.primaryContrastText : themeTokens.secondaryContrastText;
    const titleHtml = section.title.trim() ? `<div class="newsletter-festive-title-band" style="padding:22px 28px;background:${toneBackground};border-top:5px solid ${themeTokens.accent};text-align:center;"><h2 style="margin:0;color:${toneText};font-size:${themeTokens.sectionTitleSize};line-height:1.18;font-weight:900;font-family:${themeTokens.headingFont};text-transform:uppercase;">${escapeHtml(section.title.trim())}</h2></div>` : "";
    const festiveCopyHtml = `${titleHtml}<div class="newsletter-festive-body" style="padding:26px 32px 30px;background:#FFFEFA;color:#2A2924;">${buildBodyHtml(section, theme, themeTokens, {textColor:"#2A2924",textAlign:"left",fontWeight:"400"})}</div>`;
    if (section.media_layout === "stacked" || !imageHtml) {
      return `<table class="newsletter-festive-section" role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;background:#FFFEFA;">${imageHtml ? `<tr><td class="newsletter-festive-image" align="center" style="padding:22px 20px;">${imageHtml}</td></tr>` : ""}<tr><td style="padding:0;">${festiveCopyHtml}</td></tr></table>`;
    }
    const imageWidthPercent = Math.round(resolveAssistantSectionMediaRatio(section, theme) * 100);
    const imageCell = `<td class="newsletter-festive-side-cell newsletter-festive-image" style="width:${imageWidthPercent}%;vertical-align:top;padding:16px;background:#FFFEFA;">${imageHtml}</td>`;
    const copyCell = `<td class="newsletter-festive-side-cell" style="vertical-align:top;padding:0;">${festiveCopyHtml}</td>`;
    return `<table class="newsletter-festive-section" role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;background:#FFFEFA;"><tr>${section.media_layout === "right" ? copyCell : imageCell}${section.media_layout === "right" ? imageCell : copyCell}</tr></table>`;
  }

  if (section.media_layout === "stacked" || !imageHtml) {
    return `
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="${cardStyle}">
        <tr>
          <td style="padding:${themeTokens.cardPadding};">
            ${imageHtml ? `<div style="margin-bottom:16px;">${imageHtml}</div>` : ""}
            ${copyHtml}
          </td>
        </tr>
      </table>
    `.trim();
  }

  const imageWidthPercent = Math.round(resolveAssistantSectionMediaRatio(section, theme) * 100);
  const sideImageHtml = buildImageHtml(section, themeTokens, "100%");

  const imageCell = `
    <td style="width:${imageWidthPercent}%;vertical-align:top;padding:0;">
      ${sideImageHtml}
    </td>
  `.trim();
  const copyCell = `
    <td style="vertical-align:top;padding:0;">
      ${copyHtml}
    </td>
  `.trim();

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="${cardStyle}">
      <tr>
        <td style="padding:${themeTokens.cardPadding};">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">
            <tr>
              ${section.media_layout === "right" ? copyCell : imageCell}
              <td style="width:24px;padding:0;"></td>
              ${section.media_layout === "right" ? imageCell : copyCell}
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `.trim();
}

function buildIntroHtml(draft: AssistantDraftPayload, themeTokens: ReturnType<typeof resolveAssistantThemeTokens>) {
  if (!draft.intro_kicker.trim() && !draft.intro_title.trim() && !draft.intro_body.trim() && !draft.intro_image_url.trim()) {
    return "";
  }

  const isFestive = draft.theme.style === "festive";
  const isBulletin = draft.theme.style === "bulletin";

  if (isBulletin) {
    return buildBulletinIntroHtml(draft, themeTokens);
  }

  const introImageHtml = buildIntroImageHtml(
    draft,
    "100%",
    isFestive ? "0" : "18px",
    draft.intro_image_layout === "stacked" || !draft.intro_image_url.trim() ? "margin:0 auto 18px;" : ""
  );
  const introCopyHtml = `
    ${draft.intro_kicker.trim()
      ? `<div style="margin:0 0 10px;color:${themeTokens.heroBodyText};font-size:13px;letter-spacing:${themeTokens.kickerSpacing};text-transform:uppercase;font-family:${themeTokens.bodyFont};${isFestive ? "font-weight:800;text-align:center;" : ""}">${escapeHtml(draft.intro_kicker.trim())}</div>`
      : ""}
    ${draft.intro_title.trim()
      ? `<h1 style="margin:0 0 16px;color:${themeTokens.heroText};font-size:${themeTokens.heroTitleSize};line-height:1.1;font-weight:${isFestive ? "900" : "700"};font-family:${themeTokens.headingFont};${isFestive ? "text-transform:uppercase;letter-spacing:0.03em;text-align:center;" : ""}">${escapeHtml(draft.intro_title.trim())}</h1>`
      : ""}
    ${draft.intro_body.trim()
      ? `<div style="margin:0;color:${themeTokens.heroBodyText};font-size:${themeTokens.bodySize};line-height:${themeTokens.bodyLineHeight};font-family:${themeTokens.bodyFont};${isFestive ? "text-align:left;font-weight:400;" : ""}">${renderAlignedText(draft.intro_body, draft.intro_body_html, draft.text_align)}</div>`
      : ""}
  `.trim();

  if (introImageHtml && draft.intro_image_layout !== "stacked") {
    const imageWidthPercent = Math.round(resolveAssistantSectionMediaRatio({
      media_width_ratio: draft.intro_image_width_ratio,
    } as AssistantSectionDraft, draft.theme) * 100);
    const imageCell = `<td style="padding:0;vertical-align:top;width:${imageWidthPercent}%;line-height:0;font-size:0;">${introImageHtml}</td>`;
    const copyCell = `<td style="padding:0;vertical-align:top;">${introCopyHtml}</td>`;

    return `
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border-radius:${themeTokens.radius};background:${themeTokens.heroBackground};border:1px solid ${themeTokens.heroBorder};${themeTokens.heroShadow !== "none" ? `box-shadow:${themeTokens.heroShadow};` : ""}">
        <tr>
          <td style="padding:${themeTokens.cardPadding};${isFestive ? `text-align:center;border-bottom:6px solid ${themeTokens.accent};` : ""}">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">
              <tr>
                ${draft.intro_image_layout === "right" ? copyCell : imageCell}
                <td style="width:24px;padding:0;"></td>
                ${draft.intro_image_layout === "right" ? imageCell : copyCell}
              </tr>
            </table>
          </td>
        </tr>
      </table>
    `.trim();
  }

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:${themeTokens.sectionGap};border-collapse:collapse;border-radius:${themeTokens.radius};background:${themeTokens.heroBackground};border:1px solid ${themeTokens.heroBorder};${themeTokens.heroShadow !== "none" ? `box-shadow:${themeTokens.heroShadow};` : ""}">
      <tr>
        <td style="padding:${themeTokens.cardPadding};${isFestive ? `text-align:center;border-bottom:6px solid ${themeTokens.accent};` : ""}">
          ${introImageHtml}
          ${introCopyHtml}
        </td>
      </tr>
    </table>
  `.trim();
}

function buildClosingHtml(draft: AssistantDraftPayload, themeTokens: ReturnType<typeof resolveAssistantThemeTokens>) {
  if (!draft.closing_note.trim()) {
    return "";
  }

  return `
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">
      <tr>
        <td style="padding:6px 8px 0;color:${themeTokens.muted};font-size:13px;line-height:1.6;text-align:center;font-family:${themeTokens.bodyFont};">
          ${renderAlignedText(draft.closing_note, draft.closing_note_html, draft.text_align)}
        </td>
      </tr>
    </table>
  `.trim();
}

export async function renderAssistantEmailHtml(draft: AssistantDraftPayload) {
  const visibleSections = draft.sections.filter(sectionHasContent).map((section) => ({
    ...section, text_align: normalizeNewsletterTextAlignment(section.text_align) || normalizeNewsletterTextAlignment(draft.text_align) || "left",
  }));
  const previewText = buildPreviewText(draft, visibleSections);
  const themeTokens = resolveAssistantThemeTokens(draft.theme);

  return `
    <!DOCTYPE html>
    <html lang="fr">
      <head>
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="x-apple-disable-message-reformatting" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${escapeHtml(previewText)}</title>
        <style>
          @media only screen and (max-width: 520px) {
            .newsletter-email-outer { padding:20px 10px !important; }
            .newsletter-festive-title-band { padding:18px 20px !important; }
            .newsletter-festive-body { padding:22px 20px 24px !important; }
            .newsletter-festive-image { padding:18px 16px !important; }
            .newsletter-festive-side-cell { display:block !important; width:100% !important; box-sizing:border-box; }
            .newsletter-festive-side-cell img { width:auto !important; max-width:100% !important; max-height:300px; margin:0 auto; }
          }
        </style>
      </head>
      <body style="margin:0;padding:0;background:${themeTokens.canvas};font-family:${themeTokens.bodyFont};">
        <div style="display:none;overflow:hidden;line-height:1px;opacity:0;max-height:0;max-width:0;">
          ${escapeHtml(previewText)}
        </div>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;background:${themeTokens.canvas};">
          <tr>
            <td class="${draft.theme.style === "festive" ? "newsletter-email-outer" : ""}" align="center" style="padding:32px 16px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:${themeTokens.containerWidth};border-collapse:collapse;">
                <tr>
                  <td>
                    ${buildHeaderLogoHtml(draft)}
                    ${buildIntroHtml(draft, themeTokens)}
                    ${visibleSections.map((section, index) => buildSectionHtmlWithTheme(section, draft.theme, themeTokens, index)).join("\n")}
                    ${buildClosingHtml(draft, themeTokens)}
                    ${buildNewsletterFooterHtml()}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `.trim();
}

export function buildNewsletterFooterHtml() {
  return `<div data-relyo-newsletter-footer="1" style="padding:24px 16px;text-align:center;font:12px/1.7 Arial,Helvetica,sans-serif;color:#68675D;border-top:1px solid #DDDCD4;"><a href="#" style="color:#68675D;">Se désinscrire</a><br><a href="https://relyoapp.fr" style="display:inline-block;margin-top:12px;color:#68675D;text-decoration:none;">Fait avec Relyo<br><img src="/api/newsletter/brand/logo.png" alt="Relyo" width="64" style="display:block;margin:8px auto 0;width:64px;height:auto;border:0;"></a></div>`;
}
